import { Box, Stack } from '@mui/material';
import { useLayoutEffect, useState, type ReactNode } from 'react';

import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import {
  computerScreenHeight,
  computerScreenWidth,
} from '@/designs-dev/device/viewport';

// Logical screen size of the laptop mockup (16:10, matches the desktop
// MiniScreen used on handover pages). Shared with the viewport options, so an
// unframed stage at '1440 · laptop' is this screen exactly.
const SCREEN_W = computerScreenWidth;
const SCREEN_H = computerScreenHeight;
const BEZEL = 14; // Cherry bezel thickness around the screen
const BASE_OVERHANG = 1.08; // base width relative to the lid
const BASE_H = 18;
const BOTTOM_MARGIN = 24; // breathing room under the base when fitting to height

/**
 * Device mockup that wraps a desktop prototype in the KareHero brand (Cherry)
 * laptop frame — the computer counterpart of PhoneFrame. Children render at a
 * logical 1440×900. By default the laptop scales itself to fit the available
 * width and viewport height; pass `scale` to pin it instead.
 */
const ComputerFrame = ({
  children,
  scale: fixedScale,
}: {
  children?: ReactNode;
  scale?: number;
}) => {
  const [screenEl, setScreenEl] = useState<HTMLElement | null>(null);
  const [wrapEl, setWrapEl] = useState<HTMLElement | null>(null);
  const [fit, setFit] = useState(0.72);

  useLayoutEffect(() => {
    if (!wrapEl || fixedScale) return;
    const compute = () => {
      const byWidth =
        (wrapEl.clientWidth / BASE_OVERHANG - BEZEL * 2) / SCREEN_W;
      // Fit below whatever chrome sits above the frame (stage header, page
      // padding), so the whole laptop is visible without scrolling. Measured
      // from the host container, whose top doesn't move when the frame
      // rescales (the wrapper itself re-centres with every size change).
      const host = wrapEl.parentElement ?? wrapEl;
      const top = Math.max(
        0,
        host.getBoundingClientRect().top + window.scrollY,
      );
      const byHeight =
        (window.innerHeight - top - BOTTOM_MARGIN - BASE_H - BEZEL * 2) /
        SCREEN_H;
      setFit(Math.min(1, byWidth, byHeight));
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(wrapEl);
    window.addEventListener('resize', compute);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [wrapEl, fixedScale]);

  const scale = fixedScale ?? Math.max(0.4, fit);
  const lidWidth = SCREEN_W * scale + BEZEL * 2;

  return (
    <Box
      sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}
      ref={setWrapEl}
    >
      <Stack sx={{ alignItems: 'center', flexShrink: 0 }}>
        {/* lid */}
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'cherry.main',
            borderRadius: '20px 20px 0 0',
            p: `${BEZEL}px`,
            boxSizing: 'content-box',
            width: SCREEN_W * scale,
          }}
        >
          {/* camera */}
          <Box
            sx={{
              position: 'absolute',
              top: 5,
              left: '50%',
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: 'cherry.dark',
              transform: 'translateX(-50%)',
            }}
          />

          {/* screen */}
          <Box
            sx={{
              width: SCREEN_W * scale,
              height: SCREEN_H * scale,
              borderRadius: '8px',
              overflow: 'hidden',
              bgcolor: 'background.default',
            }}
          >
            <Box
              ref={setScreenEl}
              sx={{
                position: 'relative',
                width: SCREEN_W,
                height: SCREEN_H,
                overflow: 'hidden',
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <PhoneScreenContext.Provider value={screenEl}>
                {children}
              </PhoneScreenContext.Provider>
            </Box>
          </Box>
        </Box>

        {/* base, with the thumb scoop marking the hinge */}
        <Box
          sx={{
            position: 'relative',
            width: lidWidth * BASE_OVERHANG,
            height: BASE_H,
            bgcolor: 'cherry.main',
            borderRadius: '0 0 14px 14px',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              width: 140,
              height: 7,
              borderRadius: '0 0 8px 8px',
              bgcolor: 'cherry.dark',
              transform: 'translateX(-50%)',
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default ComputerFrame;
